import { MapPin, Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  Breadcrumbs,
  Button,
  Group,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  createStyles,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import { useBreadcrumbStyles } from "../styles/sharedStyles";
import ClientBadge from "../elements/ClientBadge";
import MapCenterSync from "../components/MapCenterSync";
import {
  useGetShiftSite,
  useUpdateShiftSite,
  useUpdateShiftSiteGroup,
} from "../services/api/ShiftSiteGroupsCrudApiService";
import AccessGuard from "../../../common/uac/AccessGuard";

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const DEFAULT_CENTER = { lat: -33.9, lng: 18.4 };
const DEFAULT_ZOOM = 14;
const FORM_MAX_WIDTH = 624;

const LATE_ARRIVAL_OPTIONS = [
  { value: "0", label: "0 (no grace)" },
  { value: "5", label: "5" },
  { value: "10", label: "10" },
  { value: "15", label: "15" },
  { value: "20", label: "20" },
  { value: "25", label: "25" },
  { value: "30", label: "30" },
  { value: "45", label: "45" },
  { value: "60", label: "60" },
];

const useStyles = createStyles(() => ({
  separator: {
    height: 1,
    background:
      "linear-gradient(90deg, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0.07) 100%), linear-gradient(90deg, #f2f2f2 0%, #f2f2f2 100%)",
  },
}));

/** Draws a geofence circle on the map. */
const GeofenceCircle: React.FC<{
  center: { lat: number; lng: number };
  radius: number;
}> = ({ center, radius }) => {
  const map = useMap();
  const circleRef = useRef<google.maps.Circle | null>(null);

  useEffect(() => {
    if (!map) return undefined;

    if (!circleRef.current) {
      circleRef.current = new google.maps.Circle({
        map,
        center,
        radius,
        strokeColor: "#1DD19C",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#1DD19C",
        fillOpacity: 0.15,
      });
    } else {
      circleRef.current.setCenter(center);
      circleRef.current.setRadius(radius);
    }

    return (): void => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    };
  }, [map, center, radius]);

  return null;
};

/** Address search using Places API (New). */
const AddressSearch: React.FC<{
  onSelect: (latVal: number, lngVal: number) => void;
}> = ({ onSelect }) => {
  const placesLib = useMapsLibrary("places");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<
    { placeId: string; description: string }[]
  >([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const fetchSuggestions = async (input: string): Promise<void> => {
    if (!placesLib) return;
    try {
      const { suggestions } =
        await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
        });
      setItems(
        suggestions.map((s) => ({
          placeId: s.placePrediction?.placeId || "",
          description: s.placePrediction?.text?.text || "",
        }))
      );
    } catch {
      setItems([]);
    }
  };

  const handleChange = (value: string): void => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim() || !placesLib) {
      setItems([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSelect = async (
    placeId: string,
    description: string
  ): Promise<void> => {
    setQuery(description);
    setItems([]);
    if (!placesLib) return;
    try {
      const place = new placesLib.Place({ id: placeId });
      await place.fetchFields({ fields: ["location"] });
      if (place.location) {
        onSelect(place.location.lat(), place.location.lng());
      }
    } catch {
      showNotification({
        title: "Error",
        message: "Could not retrieve place details. Please try again.",
        color: "red",
      });
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <TextInput
        label={
          <Text size="sm" weight={500}>
            Address{" "}
            <Text component="span" color="red" size="sm">
              *
            </Text>
          </Text>
        }
        placeholder="Search"
        icon={<Search size={14} />}
        value={query}
        onChange={(e) => handleChange(e.currentTarget.value)}
        size="sm"
      />
      {items.length > 0 && (
        <Paper
          shadow="md"
          radius="sm"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          {items.map((item) => (
            <Text
              key={item.placeId}
              size="sm"
              p="xs"
              role="option"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              onClick={() => handleSelect(item.placeId, item.description)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelect(item.placeId, item.description);
                }
              }}
              sx={{ "&:hover, &:focus": { backgroundColor: "#f1f3f5" } }}
            >
              {item.description}
            </Text>
          ))}
        </Paper>
      )}
    </div>
  );
};

interface FormValues {
  name: string;
  site_group_name: string;
  latitude: string;
  longitude: string;
  radius_allowance: string;
  late_arrival_threshold_minutes: string;
}

const ShiftSiteConfigPageInner: React.FC = () => {
  const { classes: bcClasses } = useBreadcrumbStyles();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { shiftSiteId } = useParams<{ shiftSiteId: string }>();

  const { data: site, isLoading } = useGetShiftSite(shiftSiteId || "");

  const updateSiteMutation = useUpdateShiftSite();
  const updateGroupMutation = useUpdateShiftSiteGroup();
  const savingRef = useRef(false);

  const form = useForm<FormValues>({
    initialValues: {
      name: "",
      site_group_name: "",
      latitude: "",
      longitude: "",
      radius_allowance: "",
      late_arrival_threshold_minutes: "",
    },
  });

  useEffect(() => {
    if (site && !savingRef.current) {
      form.setValues({
        name: site.name || "",
        site_group_name: site.site_group_name || "",
        latitude: site.latitude != null ? String(site.latitude) : "",
        longitude: site.longitude != null ? String(site.longitude) : "",
        radius_allowance:
          site.radius_allowance != null ? String(site.radius_allowance) : "",
        late_arrival_threshold_minutes:
          site.late_arrival_threshold_minutes != null
            ? String(site.late_arrival_threshold_minutes)
            : "",
      });
    }
  }, [site]); // eslint-disable-line

  const latRaw = parseFloat(form.values.latitude);
  const lngRaw = parseFloat(form.values.longitude);
  const hasCoords = Number.isFinite(latRaw) && Number.isFinite(lngRaw);
  const lat = hasCoords ? latRaw : 0;
  const lng = hasCoords ? lngRaw : 0;
  const mapCenter = hasCoords ? { lat, lng } : DEFAULT_CENTER;
  const radiusMeters = parseFloat(form.values.radius_allowance) || 0;

  const handleAddressSelect = (latVal: number, lngVal: number): void => {
    form.setFieldValue("latitude", String(latVal));
    form.setFieldValue("longitude", String(lngVal));
  };

  const handleMarkerDrag = (e: google.maps.MapMouseEvent): void => {
    if (e.latLng) {
      form.setFieldValue("latitude", String(e.latLng.lat()));
      form.setFieldValue("longitude", String(e.latLng.lng()));
    }
  };

  const handleUseCurrentLocation = (): void => {
    if (!navigator.geolocation) {
      showNotification({
        title: "Unavailable",
        message: "Geolocation is not supported by your browser.",
        color: "yellow",
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        form.setFieldValue("latitude", String(position.coords.latitude));
        form.setFieldValue("longitude", String(position.coords.longitude));
      },
      () => {
        showNotification({
          title: "Error",
          message: "Could not retrieve your current location.",
          color: "red",
        });
      }
    );
  };

  const handleSave = async (): Promise<void> => {
    if (!shiftSiteId) return;

    const siteName = form.values.name.trim();
    if (!siteName) {
      showNotification({
        title: "Validation Error",
        message: "Site name is required.",
        color: "red",
      });
      return;
    }

    const parsedLat = Math.round(parseFloat(form.values.latitude) * 1e6) / 1e6;
    const parsedLng = Math.round(parseFloat(form.values.longitude) * 1e6) / 1e6;
    const parsedRadius =
      Math.round(parseFloat(form.values.radius_allowance) * 1e2) / 1e2;
    const thresholdVal = form.values.late_arrival_threshold_minutes;
    const parsedThreshold =
      thresholdVal !== "" ? parseInt(thresholdVal, 10) : null;

    if (
      !Number.isFinite(parsedLat) ||
      !Number.isFinite(parsedLng) ||
      !Number.isFinite(parsedRadius)
    ) {
      showNotification({
        title: "Validation Error",
        message:
          "Please enter valid numeric values for coordinates and radius.",
        color: "red",
      });
      return;
    }

    // Validate group name if site belongs to a group
    const newGroupName = form.values.site_group_name.trim();
    if (site?.site_group && !newGroupName) {
      showNotification({
        title: "Validation Error",
        message: "Site group name is required.",
        color: "red",
      });
      return;
    }

    try {
      // Suppress useEffect form rehydration while mutations are in flight,
      // so cache invalidation refetches don't clobber user edits.
      savingRef.current = true;
      const groupNameChanged =
        site?.site_group && newGroupName !== site.site_group_name;
      if (groupNameChanged && site?.site_group) {
        await updateGroupMutation.mutateAsync({
          id: site.site_group,
          name: newGroupName,
        });
      }

      await updateSiteMutation.mutateAsync({
        id: shiftSiteId,
        name: siteName,
        latitude: parsedLat,
        longitude: parsedLng,
        radius_allowance: parsedRadius,
        late_arrival_threshold_minutes: parsedThreshold,
      });

      showNotification({
        title: "Success",
        message:
          "Site configuration saved. Locations without their own config will inherit these values.",
        color: "green",
      });
      navigate(`/time-attendance/shift-site/${shiftSiteId}`);
    } catch {
      savingRef.current = false;
      showNotification({
        title: "Error",
        message: "Failed to save site configuration. Please try again.",
        color: "red",
      });
    }
  };

  const handleCancel = (): void => {
    navigate(`/time-attendance/shift-site/${shiftSiteId}`);
  };

  return (
    <PageContainer>
      <LoadingOverlay visible={isLoading} />

      <PageHeading title="Time & Attendance" />

      <Breadcrumbs
        separator=">"
        classNames={{
          root: "mt-2 mb-6",
          breadcrumb: "hover:no-underline text-sm",
          separator: "text-gray-400",
        }}
      >
        <Text
          className={bcClasses.breadcrumbLink}
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/time-attendance")}
        >
          Time &amp; Attendance
        </Text>
        {site?.site_group_name && (
          <Text
            className={bcClasses.breadcrumbLink}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/time-attendance")}
          >
            {site.site_group_name}
          </Text>
        )}
        <Text
          className={bcClasses.breadcrumbLink}
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/time-attendance/shift-site/${shiftSiteId}`)}
        >
          {site?.name || "..."}
        </Text>
        <Text className={bcClasses.breadcrumbCurrent}>Site Configuration</Text>
      </Breadcrumbs>

      <Stack spacing="lg">
        {/* Site info header */}
        <Stack spacing={8}>
          <Group spacing="sm" align="center" position="apart">
            <Text weight={500} size="lg">
              {site?.name || ""}
            </Text>
            {site?.client_name && <ClientBadge clientName={site.client_name} />}
          </Group>
          {site?.site_group_name && (
            <Group spacing={4}>
              <MapPin size={16} color="#839099" />
              <Text size="sm" color="dimmed">
                {site.site_group_name}
              </Text>
            </Group>
          )}
        </Stack>

        <div className={classes.separator} />

        {/* Name editing section */}
        <Stack spacing="lg" style={{ maxWidth: FORM_MAX_WIDTH }}>
          <Stack spacing={4}>
            <Text weight={500} size="lg">
              Names
            </Text>
            <Text size="sm" color="dimmed">
              Update the site name
              {site?.site_group ? " and site group name" : ""}.
            </Text>
          </Stack>

          <TextInput
            required
            label={
              <Text size="sm" weight={500}>
                Site Name
              </Text>
            }
            placeholder="Enter site name"
            {...form.getInputProps("name")}
            size="sm"
          />

          {site?.site_group && (
            <TextInput
              required
              label={
                <Text size="sm" weight={500}>
                  Site Group Name
                </Text>
              }
              placeholder="Enter site group name"
              {...form.getInputProps("site_group_name")}
              size="sm"
            />
          )}
        </Stack>

        <div className={classes.separator} />

        <Stack spacing={4}>
          <Text weight={500} size="lg">
            Site Defaults
          </Text>
          <Text size="sm" color="dimmed">
            These defaults apply to all shift locations under this site that
            don&apos;t have their own configuration.
          </Text>
        </Stack>

        {/* Geofence section */}
        <Stack spacing="lg" style={{ maxWidth: FORM_MAX_WIDTH }}>
          <Stack spacing={4}>
            <Text weight={500} size="md">
              Default Location &amp; Geofence
            </Text>
            <Text size="sm" color="dimmed">
              Set the default GPS coordinates and geofence radius for shift
              locations under this site.
            </Text>
          </Stack>

          {googleMapsApiKey ? (
            <APIProvider apiKey={googleMapsApiKey}>
              <Stack spacing="md">
                <AddressSearch onSelect={handleAddressSelect} />

                <div
                  style={{
                    position: "relative",
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <Map
                    style={{ width: "100%", height: 400 }}
                    defaultCenter={mapCenter}
                    defaultZoom={DEFAULT_ZOOM}
                    gestureHandling="greedy"
                    mapId="site-config-map"
                  >
                    <MapCenterSync lat={lat} lng={lng} enabled={hasCoords} />
                    {hasCoords && (
                      <AdvancedMarker
                        position={{ lat, lng }}
                        draggable
                        onDragEnd={handleMarkerDrag}
                      />
                    )}
                    {hasCoords && radiusMeters > 0 && (
                      <GeofenceCircle
                        center={{ lat, lng }}
                        radius={radiusMeters}
                      />
                    )}
                  </Map>
                  <Button
                    size="xs"
                    variant="outline"
                    color="dark"
                    style={{
                      position: "absolute",
                      bottom: 12,
                      right: 12,
                      backgroundColor: "white",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                      zIndex: 1,
                    }}
                    onClick={handleUseCurrentLocation}
                  >
                    Use current
                  </Button>
                </div>
              </Stack>
            </APIProvider>
          ) : null}

          {/* Lat / Lng */}
          <Group grow spacing="sm">
            <NumberInput
              label={
                <Text size="sm" weight={500}>
                  Latitude{" "}
                  <Text component="span" color="red" size="sm">
                    *
                  </Text>
                </Text>
              }
              precision={6}
              value={
                form.values.latitude !== ""
                  ? parseFloat(form.values.latitude)
                  : undefined
              }
              onChange={(value) =>
                form.setFieldValue("latitude", String(value ?? ""))
              }
              size="sm"
            />
            <NumberInput
              label={
                <Text size="sm" weight={500}>
                  Longitude{" "}
                  <Text component="span" color="red" size="sm">
                    *
                  </Text>
                </Text>
              }
              precision={6}
              value={
                form.values.longitude !== ""
                  ? parseFloat(form.values.longitude)
                  : undefined
              }
              onChange={(value) =>
                form.setFieldValue("longitude", String(value ?? ""))
              }
              size="sm"
            />
          </Group>

          <NumberInput
            label={
              <Text size="sm" weight={500}>
                Geofence Radius (metres){" "}
                <Text component="span" color="red" size="sm">
                  *
                </Text>
              </Text>
            }
            value={
              form.values.radius_allowance !== ""
                ? parseFloat(form.values.radius_allowance)
                : undefined
            }
            onChange={(value) =>
              form.setFieldValue("radius_allowance", String(value ?? ""))
            }
            min={0}
            size="sm"
          />

          <div className={classes.separator} />

          {/* Late arrival threshold */}
          <Stack spacing={4}>
            <Text weight={500} size="md">
              Default Late Arrival Threshold
            </Text>
            <Text size="sm" color="dimmed">
              Minutes before an employee is marked as a late arrival.
            </Text>
          </Stack>

          <Select
            label={
              <Text size="sm" weight={500}>
                Threshold (minutes)
              </Text>
            }
            placeholder="Select threshold"
            data={LATE_ARRIVAL_OPTIONS}
            value={form.values.late_arrival_threshold_minutes || null}
            onChange={(value) =>
              form.setFieldValue("late_arrival_threshold_minutes", value ?? "")
            }
            size="sm"
            style={{ maxWidth: 300 }}
          />
        </Stack>

        <div className={classes.separator} />

        <Group position="right" spacing="sm">
          <Button
            variant="outline"
            color="dark"
            onClick={handleCancel}
            disabled={
              updateSiteMutation.isLoading || updateGroupMutation.isLoading
            }
            style={{ minWidth: 118 }}
          >
            Cancel
          </Button>
          <Button
            color="dark"
            onClick={handleSave}
            loading={
              updateSiteMutation.isLoading || updateGroupMutation.isLoading
            }
            style={{ minWidth: 118 }}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </PageContainer>
  );
};

const ShiftSiteConfigPage: React.FC = () => (
  <AccessGuard>
    <ShiftSiteConfigPageInner />
  </AccessGuard>
);

export default ShiftSiteConfigPage;
